import { useCallback, useState } from 'react';

import { SearchBy, SearchSettings } from '@givebacks/api/query';
import {
  ActionIcon,
  ComparisonInput,
  DatePickerInput,
  DatesRangeValue,
  MultiSelect,
  NumberInput,
  Select,
  TextInput,
  Tooltip,
} from 'ui';
import { useDebouncedValue } from 'ui/Hooks';
import { IconChevronDownTabler, IconInfo, IconSearch, IconXTabler } from 'ui/Icons';

import { useTableSearch } from '../../Search/useTableSearch';
import { useTableContext } from '../../table.context';
import {
  TableDateRangeFieldProps,
  TableMultiSelectFieldProps,
  TableSearchFieldProps,
  TableSelectFieldProps,
} from '../../types/searchFieldsTypes';
import { deserializeMultiSelect, transformStringIntoDateValue } from './deSerializers/deSeralizers';

// this input currently has a maw of 206px ... ?
const TableSearchInput = ({
  fields,
  placeHolder,
  info,
  initialValue = '',
  showInParams = true,
  searchLabel,
  ...rest
}: TableSearchFieldProps<SearchSettings>) => {
  const ctx = useTableContext();
  const [search, setSearch] = useState(initialValue);
  const [debouncedSearch] = useDebouncedValue(search, 500, { leading: false });

  useTableSearch({
    search: debouncedSearch,
    store: ctx.atomStore,
    reset: () => setSearch(''),
    fields,
    serializer: 'string',
    showInParams,
    label: searchLabel ?? rest.label,
  });

  return (
    <TextInput
      leftSection={<IconSearch size="1.1rem" stroke={1.5} />}
      rightSection={
        search ? (
          <Tooltip label="Clear">
            <ActionIcon variant="transparent" c="gray" onClick={() => setSearch('')}>
              <IconXTabler brand="tabler" />
            </ActionIcon>
          </Tooltip>
        ) : info ? (
          <Tooltip label={info}>
            <IconInfo />
          </Tooltip>
        ) : null
      }
      placeholder={placeHolder}
      value={search}
      onChange={(e) => setSearch?.(e.target.value)}
      {...rest}
    />
  );
};

const TableDateInput = ({
  fields,
  placeHolder,
  info,
  rootRef,
  initialValue,
  defaultValue,
  showInParams = true,
  searchLabel,
  ...rest
}: TableDateRangeFieldProps<SearchSettings>) => {
  const ctx = useTableContext();
  const [dateRangeValue, setDateRangeValue] = useState<DatesRangeValue | undefined>(
    transformStringIntoDateValue(initialValue) ?? defaultValue
  );

  useTableSearch({
    search: dateRangeValue,
    store: ctx.atomStore,
    reset: () => setDateRangeValue([null, null]),
    serializer: 'date-range',
    isEmptySearch: (range) => !range || (!range[0] && !range[1]),
    fields,
    label: searchLabel ?? rest.label,
    showInParams,
  });

  return (
    <Tooltip label={info} disabled={!info}>
      <DatePickerInput
        type="range"
        value={dateRangeValue}
        valueFormat="MM/DD/YYYY"
        placeholder={placeHolder ?? 'Start Date - End Date'}
        clearable
        clearButtonProps={{ onClick: () => setDateRangeValue([null, null]) }}
        popoverProps={{
          portalProps: { target: rootRef?.current ? rootRef?.current : undefined },
        }}
        {...rest}
        onChange={(value) => setDateRangeValue(value as DatesRangeValue)}
      />
    </Tooltip>
  );
};

const TableSelectInput = ({
  fields,
  placeHolder,
  data,
  rootRef,
  initialValue,
  showInParams = true,
  clearable = true,
  searchLabel,
  ...rest
}: TableSelectFieldProps<SearchSettings>) => {
  const ctx = useTableContext();
  const [search, setSearch] = useState<string | null | undefined>(
    initialValue ?? rest.defaultValue ?? undefined
  );

  const handleClear = useCallback(() => {
    if (clearable) {
      setSearch(null);
    }
  }, [clearable]);

  useTableSearch({
    search,
    store: ctx.atomStore,
    reset: handleClear,
    serializer: 'string',
    fields,
    label: searchLabel ?? rest.label,
    showInParams,
    customFormatter: (value) => {
      const foundItem = data?.find((item) => {
        if (typeof item === 'object' && 'value' in item) {
          return item?.value === value;
        }
        return item === value;
      });

      const label =
        typeof foundItem === 'object' && 'value' in foundItem ? foundItem?.label : foundItem;
      return typeof label == 'string' ? label : value;
    },
  });

  const RightSectionIcon = clearable ? IconXTabler : IconChevronDownTabler;

  return (
    <Select
      value={search}
      onChange={(value) => setSearch(value ?? '')}
      placeholder={placeHolder}
      data={data}
      clearable={clearable}
      rightSection={
        search ? (
          <Tooltip label="Clear">
            <ActionIcon size="md" variant="transparent" c="gray" onClick={handleClear}>
              <RightSectionIcon size="1.1rem" stroke={1.5} brand="tabler" />
            </ActionIcon>
          </Tooltip>
        ) : undefined
      }
      {...rest}
      comboboxProps={{
        ...rest.comboboxProps,
        portalProps: { target: rootRef?.current ? rootRef?.current : undefined },
      }}
    />
  );
};

const TableMultiSelectInput = ({
  fields,
  placeHolder,
  data,
  rootRef,
  searchBy,
  comboboxProps,
  initialValue,
  showInParams = true,
  searchLabel,
  ...rest
}: TableMultiSelectFieldProps<SearchSettings>) => {
  const ctx = useTableContext();
  const [search, setSearch] = useState<string[]>(
    deserializeMultiSelect(initialValue) ?? rest.defaultValue ?? []
  );

  const handleClear = useCallback(() => {
    setSearch([]);
  }, []);

  useTableSearch({
    search,
    store: ctx.atomStore,
    reset: handleClear,
    serializer: 'multi-select',
    isEmptySearch: (search) => search == null || (Array.isArray(search) && search.length === 0),
    fields,
    searchBy,
    label: searchLabel ?? rest.label,
    showInParams,
    customFormatter: (value) => {
      const valueArray = value.split(',');

      const label = valueArray.map((selectedValue) => {
        const foundItem = data?.find((item) => {
          if (typeof item === 'object' && 'value' in item) {
            return item?.value === selectedValue;
          }
          return item === selectedValue;
        });

        const label =
          typeof foundItem === 'object' && 'value' in foundItem ? foundItem?.label : foundItem;
        return typeof label == 'string' ? label : selectedValue;
      });
      return label.join(',');
    },
  });

  return (
    <MultiSelect
      value={search}
      onChange={(value) => setSearch(value)}
      placeholder={placeHolder}
      data={data}
      clearable
      rightSection={
        search.length ? (
          <Tooltip label="Clear">
            <ActionIcon size="md" variant="transparent" c="gray" onClick={handleClear}>
              <IconXTabler size="1.1rem" stroke={1.5} brand="tabler" />
            </ActionIcon>
          </Tooltip>
        ) : undefined
      }
      {...rest}
      comboboxProps={{
        ...comboboxProps,
        portalProps: { target: rootRef?.current ? rootRef?.current : undefined },
      }}
    />
  );
};

const TableSearchNumberInput = ({
  fields,
  placeHolder,
  info,
  initialValue = '',
  showInParams = true,
  searchLabel,
  ...rest
}: TableSearchFieldProps<SearchSettings>) => {
  const ctx = useTableContext();
  const [search, setSearch] = useState<string | number>(initialValue);
  const [debouncedSearch] = useDebouncedValue(search, 500, { leading: false });

  useTableSearch({
    search: String(debouncedSearch),
    store: ctx.atomStore,
    reset: () => setSearch(''),
    fields,
    serializer: 'string',
    label: searchLabel ?? rest.label,
    showInParams,
  });

  return (
    <NumberInput
      leftSection={<IconSearch size="1.1rem" stroke={1.5} />}
      rightSection={
        search ? (
          <Tooltip label="Clear">
            <ActionIcon variant="transparent" c="gray" onClick={() => setSearch('')}>
              <IconXTabler brand="tabler" />
            </ActionIcon>
          </Tooltip>
        ) : info ? (
          <Tooltip label={info}>
            <IconInfo />
          </Tooltip>
        ) : null
      }
      placeholder={placeHolder}
      value={search}
      onChange={(value) => setSearch?.(value)}
      allowDecimal
      decimalScale={2}
      {...rest}
    />
  );
};

const TableComparisonInput = ({
  fields,
  placeHolder,
  info,
  initialValue = '',
  showInParams = true,
  searchBy,
  searchLabel,
  ...rest
}: TableSearchFieldProps<SearchSettings>) => {
  const ctx = useTableContext();
  const [search, setSearch] = useState(initialValue);
  const [_searchBy, setSearchBy] = useState<SearchBy | undefined>(searchBy);
  const [debouncedSearch] = useDebouncedValue(search, 500, { leading: false });

  useTableSearch({
    search: debouncedSearch,
    store: ctx.atomStore,
    reset: () => setSearch(''),
    fields,
    serializer: 'string',
    showInParams,
    label: searchLabel ?? rest.label,
    searchBy: _searchBy,
  });

  return (
    <ComparisonInput
      rightSection={
        search ? (
          <Tooltip label="Clear">
            <ActionIcon variant="transparent" c="gray" onClick={() => setSearch('')}>
              <IconXTabler brand="tabler" />
            </ActionIcon>
          </Tooltip>
        ) : info ? (
          <Tooltip label={info}>
            <IconInfo />
          </Tooltip>
        ) : null
      }
      placeholder={placeHolder}
      value={search}
      onChange={(e) => setSearch?.(e.target.value)}
      onOperatorChange={setSearchBy}
      {...rest}
    />
  );
};

export {
  TableComparisonInput,
  TableDateInput,
  TableMultiSelectInput,
  TableSearchInput,
  TableSearchNumberInput,
  TableSelectInput,
};
